// Angular Files
import { BrowserModule, Title } from '@angular/platform-browser';
import {
 ErrorHandler, NgModule, inject, provideAppInitializer
} from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { A11yModule } from '@angular/cdk/a11y';

// Angular Material Files
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';

// Other External Files
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';

// Root App Component Files

// Teller Online Files
import { environment } from 'apps/admin-portal/src/environments/environment';
import { config } from 'apps/admin-portal/src/config/config';

// Teller Online Library Files
import { TellerOnlineIconsModule, TellerOnlineIconsService } from 'teller-online-libraries/icons';
import {
    API_BASE_URL,
    MAT_FORM_FIELD_OUTLINE_DEFAULTS,
    TellerOnlineAppService,
    TellerOnlineCoreModule,
    TellerOnlineErrorHandlerService,
    TellerOnlineSiteMetadataService,
    TellerOnlineXsrfService,
    TellerOnlineEngagementHostComponent,
    TellerOnlineEngagementService,
    TellerOnlineRumBootstrapService
} from 'teller-online-libraries/core';
import { TellerOnlineHelpModule } from 'teller-online-libraries/help';
import { TellerOnlineSharedModule } from 'teller-online-libraries/shared';
import { AuthService, SessionExpiryEnum } from './core/services';
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        AppRoutingModule,
        TellerOnlineEngagementHostComponent,
        BrowserModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.production,
            registrationStrategy: 'registerImmediately'
        }),
        CoreModule,
        SharedModule,
        TellerOnlineIconsModule,
        TellerOnlineSharedModule,
        TellerOnlineCoreModule,
        TellerOnlineHelpModule,
        BrowserAnimationsModule,
        // Angular Material
        MatButtonModule,
        MatSidenavModule,
        MatIconModule,
        MatToolbarModule,
        A11yModule,
        // Provided here rather than in the lazy hold-page module: AuthService is created at
        // startup and injects OAuthService, and it must share one instance (and one token
        // store) with the hold page so its logOut clears the tokens the hold page reads.
        OAuthModule.forRoot()
    ],
    providers: [
        Title,
        { provide: OAuthStorage, useFactory: oauthStorageFactory },
        TellerOnlineIconsService,
        MAT_FORM_FIELD_OUTLINE_DEFAULTS,
        { provide: ErrorHandler, useClass: TellerOnlineErrorHandlerService },
        { provide: API_BASE_URL, useValue: config.adminPortalApiUrl },
        provideAppInitializer(() => {
            const rumBootstrap = inject(TellerOnlineRumBootstrapService);
            const engagement = inject(TellerOnlineEngagementService);
            const initializerFn = loadRequiredServices(
                inject(TellerOnlineAppService),
                inject(TellerOnlineSiteMetadataService),
                inject(AuthService),
                inject(TellerOnlineErrorHandlerService),
                inject(TellerOnlineXsrfService),
                inject(API_BASE_URL),
                () => {
                    // Must never throw into APP_INITIALIZER
                    try {
                        rumBootstrap.start('admin');
                        engagement.start('admin');
                    } catch (err) {
                        console.warn('RUM start failed', err);
                    }
                });

            return initializerFn();
        }),
        { provide: HTTP_INTERCEPTORS, useClass: AuthService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TellerOnlineXsrfService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: TellerOnlineAppService, multi: true },
        provideHttpClient(withInterceptorsFromDi()),
    ]
})
export class AppModule {}

export function oauthStorageFactory(): OAuthStorage {
    return localStorage;
}

export function loadRequiredServices(
    appService: TellerOnlineAppService,
    siteMetadataService: TellerOnlineSiteMetadataService,
    authService: AuthService,
    errorHandlerService: TellerOnlineErrorHandlerService,
    xsrfService: TellerOnlineXsrfService,
    apiBaseUrl: string,
    onMetadataLoaded: () => void) {
    return () => new Promise(resolve => {
        // Capture ?tellerLogon before the current user check below, which can
        // redirect to AD and strip the parameter off the url
        authService.captureTellerLogonFromUrl();

        // initialize all the shared services with the correct base url (because the injection token will not have run yet at this point)
        errorHandlerService.init(environment, apiBaseUrl);
        siteMetadataService.init(apiBaseUrl);
        xsrfService.init(apiBaseUrl);
        /* Immediately check if we need to reload the page,
        because we are immediatelly registering the service worker this should finish before the other requests finish so we shouldn't see any blips (double load)
        if there is a blip though, the speed should be so fast that it shouldn't be a big deal*/
        appService.checkForUpdates();
        // setup which pages do not use rail navigation so we can properly adjust the layout of the app
        appService.setNonRailPages(['/dashboard', '/sign-in']);

        // Check for AD Config, and load site metadata, in parallel. getUser() depends on
        // adConfigModel being resolved, so wait for both before running it. Neither
        // failure should block bootstrap - a failed AD config fetch is covered by the
        // ?. checks elsewhere (getAdConfig() logs it internally), and a failed metadata
        // load just leaves defaults in place (logged here since nothing else will).
        Promise.all([
            authService.getAdConfig().catch(() => undefined),
            siteMetadataService.load().catch(err => logAndSwallow(errorHandlerService, err))
        ]).then(() => {
            onMetadataLoaded();
            getUser(authService, resolve);
        }).catch(err => {
            // Last-resort net: if getUser() itself throws synchronously, resolve() would
            // otherwise never fire and bootstrap would hang forever.
            logAndSwallow(errorHandlerService, err);
            accesibility_contentLoaded();
            resolve(true);
        });
    });
}

function getUser(authService: AuthService, resolve) {
    const cookieName = 'externalAuthToken';
    const cookie = getCookie(cookieName);

    if (cookie) {
        // Store the token in localStorage (same as normal sign-in flow)
        window.localStorage.setItem('token', cookie);

        // Delete the cookie since we've consumed it
        deleteCookie(cookieName);
    }

    // Skip the current-user probe when landing on the OAuth callback. The SPA
    // has not exchanged the code for a Teller session yet, so /current-user
    // will 401; letting that flow into localSignOut() would navigate to
    // /hold-page and strip ?code=, restarting the federation loop.
    const onAuthCallback = window.location.pathname === '/hold-page'
        && /[?&]code=/.test(window.location.search);

    if (onAuthCallback) {
        accesibility_contentLoaded();
        resolve(true);
        return;
    }

    // Get the current user, if no user is returned we know we arne't authenticated
    authService.getCurrentUser().finally(() => {
        if (authService.isSessionExpired() === SessionExpiryEnum.Expired) {
            authService.localSignOut();
        }
        accesibility_contentLoaded();
        resolve(true);
    });
}

function accesibility_contentLoaded() {
    document.querySelector('body').setAttribute('aria-busy', 'false');
}

/** Logs a bootstrap error instead of dropping it silently, without ever throwing back out -
 * handleError() rethrows when appService.debug is on, which would defeat the swallow. */
function logAndSwallow(
    errorHandlerService: TellerOnlineErrorHandlerService,
    err: unknown
): undefined {
    try {
        errorHandlerService.handleError(err, false);
    } catch {
        // Best-effort only - never let the logging attempt itself throw.
    }
    return undefined;
}

/**
 * Gets a cookie value by name.
 */
function getCookie(name: string): string | null {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.indexOf(nameEQ) === 0) {
            return cookie.substring(nameEQ.length);
        }
    }
    return null;
}

/**
 * Deletes a cookie by name.
 */
function deleteCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}
